import { fetchJsonApi, toApiError, type PaginatedResponse, type PaginationMeta } from "@/shared/api/common";
import { http } from "@/shared/api/http";
import { type ReferentPriceCurrency } from "@/shared/config/constants";

export type ReferentPriceEntry = {
  id: string;
  wholesalePrice: number | null;
  retailPrice: number | null;
  enforceAt: string | null;
  crawledAt: string | null;
  updatedAt: string | null;
  registrationNumber: string | null;
};

export type ReferentPriceEntryApi = {
  id?: string | null;
  wholesale_price?: number | null;
  retail_price?: number | null;
  enforce_at?: string | null;
  crawled_at?: string | null;
  updated_at?: string | null;
  registration_number?: string | null;
};

export function toReferentPriceEntry(api: ReferentPriceEntryApi): ReferentPriceEntry {
  return {
    id: api.id ?? "",
    wholesalePrice: api.wholesale_price ?? null,
    retailPrice: api.retail_price ?? null,
    enforceAt: api.enforce_at ?? null,
    crawledAt: api.crawled_at ?? null,
    updatedAt: api.updated_at ?? null,
    registrationNumber: api.registration_number ?? null,
  };
}

export type ReferentPrice = {
  id: string;
  medicine_id: string;
  registration_number: string | null;
  wholesale_price: number | null;
  retail_price: number | null;
  enforce_at?: string | null;
  updated_at: string | null;
};

type GetReferentPriceResponse = {
  data: ReferentPrice | ReferentPrice[] | null;
};

export async function getReferentPriceEntries(medicineId: string | number): Promise<ReferentPriceEntry[]> {
  const response = await fetchJsonApi<GetReferentPriceResponse>(
    `/analytics/referent-prices?medicine_id=${encodeURIComponent(String(medicineId))}`,
  );
  const raw = response.data;
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map((item) =>
    toReferentPriceEntry({
      id: item.id,
      wholesale_price: item.wholesale_price,
      retail_price: item.retail_price,
      enforce_at: item.enforce_at,
      updated_at: item.updated_at,
      registration_number: item.registration_number,
    }),
  );
}

export type ReferentPriceVendor = {
  id: string;
  name: string | null;
  countryName: string | null;
};

export type ReferentPriceMedicine = {
  id: string;
  name: string | null;
  tradeName: string | null;
  internationalName: string | null;
  dosageForm: string | null;
  strength: string | null;
  volume: string | null;
  pack: number | null;
  vendor: ReferentPriceVendor | null;
};

export type ReferentPriceRow = {
  id: string;
  pharmId: string | null;
  tradeName: string;
  internationalName: string | null;
  manufacturer: string | null;
  packageName: string;
  registrationNumber: string | null;
  currency: ReferentPriceCurrency;
  referentPrice: number | null;
  wholesalePrice: number | null;
  retailPrice: number | null;
  packageId: string | null;
  sourceRowIndex: number;
  createdAt: string | null;
  updatedAt: string | null;
  enforceAt: string | null;
  referentPrices: ReferentPriceEntry[];
  medicineId: string | null;
  medicine: ReferentPriceMedicine | null;
};

export type ReferentPriceSortField =
  | "trade_name"
  | "international_name"
  | "manufacturer"
  | "referent_price"
  | "wholesale_price"
  | "retail_price"
  | "currency";

export type ReferentPriceSortOrder = "asc" | "desc";

export type ReferentPriceMatchStatus = "all" | "matched" | "unmatched";

export type ReferentPricesQueryParams = {
  page?: number;
  limit?: number;
  search?: string;
  focusMedicineId?: string | null;
  matchStatus?: ReferentPriceMatchStatus | null;
  sortBy?: ReferentPriceSortField | null;
  sortOrder?: ReferentPriceSortOrder | null;
};

type ReferentPriceVendorApi = {
  id: string;
  name: string | null;
  country_name: string | null;
};

type ReferentPriceMedicineApi = {
  id: string;
  name: string | null;
  trade_name: string | null;
  international_name: string | null;
  dosage_form: string | null;
  strength: string | null;
  volume: string | null;
  pack: number | null;
  vendor: ReferentPriceVendorApi | null;
};

type ReferentPriceRowApi = {
  id: string;
  pharm_id: string | null;
  trade_name: string;
  international_name: string | null;
  manufacturer: string | null;
  package_name: string;
  registration_number: string | null;
  currency: ReferentPriceCurrency;
  referent_price: number | null;
  wholesale_price: number | null;
  retail_price: number | null;
  package_id: string | null;
  source_row_index: number;
  created_at: string | null;
  updated_at: string | null;
  enforce_at?: string | null;
  referent_prices?: ReferentPriceEntryApi[] | null;
  medicine_id: string | null;
  medicine: ReferentPriceMedicineApi | null;
};

function toReferentPriceRow(row: ReferentPriceRowApi): ReferentPriceRow {
  const entries = Array.isArray(row.referent_prices)
    ? row.referent_prices.map((entry) =>
        toReferentPriceEntry({ registration_number: row.registration_number, ...entry }),
      )
    : [
        toReferentPriceEntry({
          id: row.id,
          wholesale_price: row.wholesale_price,
          retail_price: row.retail_price,
          enforce_at: row.enforce_at,
          updated_at: row.updated_at,
          registration_number: row.registration_number,
        }),
      ];

  return {
    id: row.id,
    pharmId: row.pharm_id,
    tradeName: row.trade_name,
    internationalName: row.international_name,
    manufacturer: row.manufacturer,
    packageName: row.package_name,
    registrationNumber: row.registration_number,
    currency: row.currency,
    referentPrice: row.referent_price,
    wholesalePrice: row.wholesale_price,
    retailPrice: row.retail_price,
    packageId: row.package_id,
    sourceRowIndex: row.source_row_index,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    enforceAt: row.enforce_at ?? null,
    referentPrices: entries,
    medicineId: row.medicine_id,
    medicine: row.medicine
      ? {
          id: row.medicine.id,
          name: row.medicine.name,
          tradeName: row.medicine.trade_name,
          internationalName: row.medicine.international_name,
          dosageForm: row.medicine.dosage_form,
          strength: row.medicine.strength,
          volume: row.medicine.volume,
          pack: row.medicine.pack,
          vendor: row.medicine.vendor
            ? {
                id: row.medicine.vendor.id,
                name: row.medicine.vendor.name,
                countryName: row.medicine.vendor.country_name,
              }
            : null,
        }
      : null,
  };
}

export async function fetchReferentPrices(
  params: ReferentPricesQueryParams = {},
): Promise<{ data: ReferentPriceRow[]; pagination: PaginationMeta }> {
  const normalizedSearch = typeof params.search === "string" ? params.search.trim() : "";

  try {
    const response = await http.get<PaginatedResponse<ReferentPriceRowApi>>("/analytics/referent-prices/list", {
      params: {
        page: params.page,
        limit: params.limit,
        q: normalizedSearch || undefined,
        focus_medicine_id: params.focusMedicineId || undefined,
        match_status: params.matchStatus && params.matchStatus !== "all" ? params.matchStatus : undefined,
        sort_by: params.sortBy ?? undefined,
        sort_order: params.sortOrder ?? undefined,
      },
      paramsSerializer: { indexes: null },
    });

    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    const page = params.page ?? 1;
    const limit = params.limit ?? 25;

    return {
      data: rows.map(toReferentPriceRow),
      pagination: response.data?.pagination ?? {
        page,
        limit,
        total: rows.length,
        totalPages: Math.max(1, Math.ceil(rows.length / limit)),
      },
    };
  } catch (error) {
    throw toApiError(error, "Unable to load referent prices.");
  }
}
